import React, { useState } from "react";
import type { ImageFile } from "../../pages/Home";
import { EditModal } from "./EditModal";
import { FiDownload } from "react-icons/fi";
import { CiEdit, CiTrash } from "react-icons/ci";
import { BsFillTrash3Fill } from "react-icons/bs";
import { BiSolidMessageSquareEdit } from "react-icons/bi";
import { FiEdit } from "react-icons/fi";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";



interface ImagesProps {
  images: ImageFile[];
  onDelete: (id: number) => void;
}

export function Images({ images, onDelete }: ImagesProps) {
  return (
    <div>
      <h2 className="hidden text-gray-800 text-xl font-poppins font-semibold mb-4">
        Images: {images.length}
      </h2>

      {/* Horizontal slider on mobile, flex-wrap grid on desktop */}
      <div
        className="
          flex 
          gap-6 
          overflow-x-auto 
          pb-4 
          lg:flex-wrap 
          lg:justify-center 
          lg:overflow-x-visible
          justify-center
          
        "
      >
        {images.map((image) => {
          const Wrapper = image.file.type.includes("video") ? Video : ImageSpot;
          return (
            <div
              key={image.id}
              className="flex-shrink-0 w-[330px] h-[464px] lg:w-[330px] lg:px-6"
            >
              <Wrapper image={image} onDelete={onDelete} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Video({ video }: { video: ImageFile }) {
  const url = URL.createObjectURL(video.file);
  return (
    <div className="bg-white rounded-lg shadow-md p-3">
      <video
        className="rounded-lg aspect-square object-cover"
        loop
        muted
        autoPlay
        src={url}
      ></video>
    </div>
  );
}

interface ImageSpotProps {
  image: ImageFile;
  onDelete: (id: number) => void;
}

function ImageSpot({ image, onDelete }: ImageSpotProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [processedImageUrl, setProcessedImageUrl] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const url = URL.createObjectURL(image.file);
  const processedURL = image.processedFile
    ? URL.createObjectURL(image.processedFile)
    : "";
  const isProcessing = !image.processedFile;

  const handleEditSave = (editedImageUrl: string) => {
    setProcessedImageUrl(editedImageUrl);
  };

  const transparentBg = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQBAMAAADt3eJSAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAGUExURb+/v////5nD/3QAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAUSURBVBjTYwABQSCglEENMxgYGAAynwRB8BEAgQAAAABJRU5ErkJggg==")`;

  return (
    <div className="bg-white shadow-md overflow-hidden rounded-[24px] relative w-[330px]">
      <div className="relative">
        {/* Three-dot menu button */}
        {!isProcessing && (
          <button
            onClick={() => setMenuOpen((prev) => !prev)}
            className="absolute top-2 right-2 z-20 p-2 bg-white/20 backdrop-blur-md rounded-full border-2 border-gary/90 hover:bg-white/10"
          >
            <PiDotsThreeOutlineVerticalFill className="text-[#242424] w-5 h-5" />
          </button>
        )}

        {isProcessing ? (
          <div className="relative">
            <img
              className="h-[464px] w-[330px] aspect-square object-cover opacity-50 transition-opacity duration-200"
              src={url}
              alt={`Processing image ${image.id}`}
            />
            <div className="absolute inset-0 flex items-center justify-center ">
              <div className="bg-black bg-opacity-50 px-4 py-2 rounded-lg">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                <span className="text-white font-medium font-poppins">Processing...</span>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="aspect-square h-[464px] w-[330px]"
            style={{
              background: transparentBg,
              backgroundRepeat: "repeat",
            }}
          >
            <img
              className="h-[464px] w-[330px] object-cover transition-opacity duration-200"
              src={processedImageUrl || processedURL}
              alt={`Processed image ${image.id}`}
            />
          </div>
        )}
      </div>

      {/* Dropdown Menu */}
      {menuOpen && !isProcessing && (
        <div className="absolute top-12 right-2 z-30 w-48 rounded-xl backdrop-blur-md bg-white/20 border border-white/30 shadow-lg p-3 space-y-2">
          <button
            onClick={() => {
              onDelete(image.id);
              setMenuOpen(false);
            }}
            className="w-full font-poppins flex items-center gap-2 px-2 py-2 text-[#242424] hover:text-white hover:bg-gray-800/30 rounded-md"
          >
            <BsFillTrash3Fill />
            Delete
          </button>
          <button
            onClick={() => {
              setIsEditModalOpen(true);
              setMenuOpen(false);
            }}
            className="w-full  font-poppins flex items-center gap-2 px-2 py-2 text-[#242424] hover:text-white hover:bg-gray-800/30 rounded-md"
          >
            <FiEdit />
            Edit
          </button>
          <a
            href={processedImageUrl || processedURL}
            download={`processed-${image.id}.png`}
            onClick={() => setMenuOpen(false)}
            className="w-full font-poppins flex items-center gap-2 px-2 py-2 text-[#242424] hover:text-white hover:bg-gray-800/30 rounded-md"
          >
            <FiDownload />
            Download
          </a>
        </div>
      )}

      <EditModal
        image={image}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleEditSave}
      />
    </div>
  );
}

